import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, MoreHorizontal, Trash2, Edit, Package, ToggleLeft, ToggleRight } from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Skeleton } from '../ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { ProductForm } from './ProductForm'
import { toast } from '../../hooks/useToast'
import { getApiError } from '../../lib/api'
import {
  useProducts,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
} from '../../hooks/useProducts'
import { Product, ProductCreate, ProductUpdate } from '../../types/product'

export function ProductList() {
  const { t } = useTranslation()
  const { data: products = [], isLoading } = useProducts()
  const createMutation = useCreateProduct()
  const updateMutation = useUpdateProduct()
  const deleteMutation = useDeleteProduct()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)

  const handleCreate = async (data: ProductCreate | ProductUpdate) => {
    try {
      await createMutation.mutateAsync(data as ProductCreate)
      toast({
        title: t('settings.products.created'),
        description: t('settings.products.createdDesc'),
      })
      setIsFormOpen(false)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleUpdate = async (data: ProductCreate | ProductUpdate) => {
    if (!editingProduct) return
    try {
      await updateMutation.mutateAsync({
        id: editingProduct.id,
        data: data as ProductUpdate,
      })
      toast({
        title: t('settings.products.updated'),
        description: t('settings.products.updatedDesc'),
      })
      setEditingProduct(null)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('settings.products.confirmDelete'))) return
    try {
      await deleteMutation.mutateAsync(id)
      toast({
        title: t('settings.products.deleted'),
        description: t('settings.products.deletedDesc'),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleToggleActive = async (product: Product) => {
    try {
      await updateMutation.mutateAsync({
        id: product.id,
        data: { isActive: !product.isActive },
      })
      toast({
        title: product.isActive
          ? t('settings.products.deactivated')
          : t('settings.products.activated'),
        description: product.isActive
          ? t('settings.products.deactivatedDesc')
          : t('settings.products.activatedDesc'),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-60" />
            </CardHeader>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">{t('settings.products.title')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('settings.products.description')}
          </p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('settings.products.add')}
        </Button>
      </div>

      {products.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <Package className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">{t('settings.products.empty')}</p>
            <Button className="mt-4" onClick={() => setIsFormOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('settings.products.addFirst')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {products.map((product) => (
            <Card key={product.id} className={!product.isActive ? 'opacity-60' : ''}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div className="space-y-1">
                  <CardTitle className="text-base flex items-center gap-2">
                    {product.name}
                    {!product.isActive && (
                      <Badge variant="outline">{t('settings.products.inactive')}</Badge>
                    )}
                  </CardTitle>
                  {product.description && (
                    <CardDescription className="line-clamp-2">
                      {product.description}
                    </CardDescription>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditingProduct(product)}>
                      <Edit className="mr-2 h-4 w-4" />
                      {t('common.edit')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleToggleActive(product)}>
                      {product.isActive ? (
                        <>
                          <ToggleLeft className="mr-2 h-4 w-4" />
                          {t('settings.products.deactivate')}
                        </>
                      ) : (
                        <>
                          <ToggleRight className="mr-2 h-4 w-4" />
                          {t('settings.products.activate')}
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => handleDelete(product.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('common.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground block text-xs">
                      {t('settings.products.price')}
                    </span>
                    <span className="font-medium">{formatCurrency(product.price)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-xs">
                      {t('settings.products.vatRate')}
                    </span>
                    <span>{product.vatRate}%</span>
                  </div>
                  {product.withholdingRate > 0 && (
                    <div>
                      <span className="text-muted-foreground block text-xs">
                        {t('settings.products.withholdingRate')}
                      </span>
                      <span>{product.withholdingRate}%</span>
                    </div>
                  )}
                  {product.unit && (
                    <div>
                      <span className="text-muted-foreground block text-xs">
                        {t('settings.products.unit')}
                      </span>
                      <span>{product.unit}</span>
                    </div>
                  )}
                  {product.sku && (
                    <div>
                      <span className="text-muted-foreground block text-xs">
                        {t('settings.products.sku')}
                      </span>
                      <span className="font-mono text-xs">{product.sku}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ProductForm
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      <ProductForm
        open={!!editingProduct}
        onClose={() => setEditingProduct(null)}
        onSubmit={handleUpdate}
        product={editingProduct}
        isLoading={updateMutation.isPending}
      />
    </div>
  )
}
